import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import { MaintenancePage, FirePage, MachinesPage, WelfarePage, FirstAidPage, LaddersPage, LegionellaPage, AsrsPage } from "./pages/Modules";
import SiteSetup from "./pages/SiteSetup";
import Audits from "./pages/Audits";
import PathwayRunner from "./pages/PathwayRunner";
import PathwayBuilder from "./pages/PathwayBuilder";
import QuestionLibrary from "./pages/QuestionLibrary";
import AreaLabels from "./pages/AreaLabels";
import AssetLabels from "./pages/AssetLabels";
import MyChecks from "./pages/MyChecks";
import Training from "./pages/Training";
import TrainingSession from "./pages/TrainingSession";
import InspectionRunner from "./pages/InspectionRunner";
import Coshh from "./pages/Coshh";
import Incidents from "./pages/Incidents";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Scan from "./pages/Scan";
import Sites from "./pages/Sites";
import GroupDashboard from "./pages/GroupDashboard";
import SdsPublic from "./pages/SdsPublic";
import CoursePlayer from "./pages/CoursePlayer";
import { SiteProvider } from "./lib/site";
import { useTheme } from "./lib/theme";

const ThemedToaster = () => <Toaster theme={useTheme().theme} position="top-center" richColors />;

const Protected = () => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-slate-500" data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
};

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  }, []);
  return (
    <AuthProvider>
      <SiteProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sds/:token" element={<SdsPublic />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/fire" element={<FirePage />} />
            <Route path="/coshh" element={<Coshh />} />
            <Route path="/puwer" element={<MachinesPage />} />
            <Route path="/welfare" element={<WelfarePage />} />
            <Route path="/first-aid" element={<FirstAidPage />} />
            <Route path="/ladders" element={<LaddersPage />} />
            <Route path="/incidents" element={<Incidents />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/users" element={<Users />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/group" element={<GroupDashboard />} />
            <Route path="/legionella" element={<LegionellaPage />} />
            <Route path="/asrs" element={<AsrsPage />} />
            <Route path="/site-setup" element={<SiteSetup />} />
            <Route path="/audits" element={<Audits />} />
            <Route path="/pathway/:id" element={<PathwayRunner />} />
            <Route path="/pathway-builder" element={<PathwayBuilder />} />
            <Route path="/question-library" element={<QuestionLibrary />} />
            <Route path="/pathway-builder/:id" element={<PathwayBuilder />} />
            <Route path="/area-labels" element={<AreaLabels />} />
            <Route path="/asset-labels" element={<AssetLabels />} />
            <Route path="/my-checks" element={<MyChecks />} />
            <Route path="/training" element={<Training />} />
            <Route path="/training/course/:cid" element={<CoursePlayer />} />
            <Route path="/training/:id" element={<TrainingSession />} />
            <Route path="/inspect/:template" element={<InspectionRunner />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </SiteProvider>
      <ThemedToaster />
    </AuthProvider>
  );
}

export default App;
